import React from 'react'
import { Button } from 'react-bootstrap'
import { Organization } from 'interfaces/organization'
import { User } from 'interfaces/user'
import useUpdateOrganizationAssignments from 'hooks/use-update-organization-assignments'

interface SaveButtonProps {
  org: Organization
  selectedAssignments: Record<
    string,
    { account_manager_user_id?: string; sale_rep_id?: string }
  >
  internalAdmins: User[]
  onSaveSuccess?: () => void
  isUpdatedPage?: boolean
}

export const SaveButton: React.FC<SaveButtonProps> = ({
  org,
  selectedAssignments,
  internalAdmins,
  onSaveSuccess,
  isUpdatedPage = false,
}) => {
  const updateMutation = useUpdateOrganizationAssignments()
  const assignments = selectedAssignments[org.id]

  const validUserIds = new Set(
    internalAdmins?.map((u) => u.id.toString()) || []
  )

  const finalAccountManagerId =
    assignments?.account_manager_user_id ??
    org.account_manager_user_id?.toString()
  const finalSalesRepId =
    assignments?.sale_rep_id ??
    org.organization_subscription?.sale_rep_id?.toString()

  const hasActualChanges =
    assignments &&
    ((assignments.account_manager_user_id !== undefined &&
      assignments.account_manager_user_id !==
        org.account_manager_user_id?.toString()) ||
      (assignments.sale_rep_id !== undefined &&
        assignments.sale_rep_id !==
          org.organization_subscription?.sale_rep_id?.toString()))

  const bothFieldsSelected =
    finalAccountManagerId &&
    finalSalesRepId &&
    validUserIds.has(finalAccountManagerId) &&
    validUserIds.has(finalSalesRepId)

  const shouldShowSaveButton = isUpdatedPage
    ? hasActualChanges
    : bothFieldsSelected

  const handleSave = async () => {
    if (!assignments) return

    try {
      const accountManagerId =
        assignments?.account_manager_user_id ??
        org.account_manager_user_id?.toString()
      const saleRepId =
        assignments?.sale_rep_id ??
        org.organization_subscription?.sale_rep_id?.toString()

      const updateData: any = {
        account_manager_user_id:
          accountManagerId === ''
            ? null
            : validUserIds.has(accountManagerId)
            ? accountManagerId
            : null,
        sale_rep_id:
          saleRepId === ''
            ? null
            : saleRepId && validUserIds.has(saleRepId)
            ? saleRepId
            : null,
      }

      await updateMutation.mutateAsync({
        orgId: org.id,
        assignments: updateData,
      })

      onSaveSuccess?.()
    } catch (error) {
      console.error('Error saving assignments:', error)
    }
  }

  if (!shouldShowSaveButton) return null

  return (
    <Button
      size="sm"
      variant="success"
      onClick={handleSave}
      disabled={updateMutation.isLoading}
      style={{
        backgroundColor: updateMutation.isLoading ? '#6c757d' : '#28a745',
        borderColor: updateMutation.isLoading ? '#6c757d' : '#28a745',
        color: 'white',
      }}
    >
      {updateMutation.isLoading ? 'Saving...' : 'Save'}
    </Button>
  )
}
